import { useState, useRef, useMemo, useEffect, MouseEventHandler } from "react";
// Helpers
import { getFormatUTCDate } from "@/Common/helpers/Dates";
import { HEADER_CLASS_NAME, ID_GRID_COLDEF } from "@/Washouts/constants";
import { errorLogger, getError } from "@/Common/helpers/logger";
import { getCellClassNameFn, isFooterCell } from "@/Common/helpers/Tables";
// Components
import AddCellIssues from "@/Layouts/components/Tables/DataSmartTable/additional/AddCellIssues";
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
// Materials
import { GridCellProps, GridColDef, useGridApiRef } from "@mui/x-data-grid-premium";
import { Nullable } from "@/Common/interfaces/types";
import { ChargesTableProps } from "@/Washouts/types/finance";

// Types
import { Issue } from "@/Common/types/tables";
import { DATE_FORMAT } from "@/Constants/date";

function getInitialColumns(reference: string, issues: Issue[]): GridColDef[] {
	return [
		ID_GRID_COLDEF,
		{
			field: "saleDate",
			headerName: "Sale Date",
			type: "date",
			width: 63,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, { noWrap: true }, {}, reference),
			valueFormatter: (value) => getFormatUTCDate(value, DATE_FORMAT.usDateSlashShortYear),
			renderCell: (params) => (
				<TypographyCell reference={reference} params={params} issues={issues} hints={{ noWrap: true }}>
					{isFooterCell(params) ? "Other Chargebacks Total Gross" : null}
				</TypographyCell>
			),
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "chargebackDate",
			headerName: "C.B. Date",
			width: 63,
			type: "date",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			valueFormatter: (value) => getFormatUTCDate(value, DATE_FORMAT.usDateSlashShortYear),
			renderCell: (params) => <TypographyCell reference={reference} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "type",
			headerName: "Type",
			width: 60,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell reference={reference} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "stockNumber",
			headerName: "Stock#",
			width: 70,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell reference={reference} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "chargebackInvoiceAmount",
			headerName: "Invoice Amount",
			width: 86,
			type: "number",
			align: "right",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell render="absolute" reference={reference} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "saleGross",
			headerName: "Sale Gross",
			width: 86,
			type: "number",
			align: "right",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell render="absolute" reference={reference} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "saleType",
			headerName: "Sale Type",
			width: 55,
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell reference={reference} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "chargebackAmount",
			headerName: "C.B. Gross",
			width: 86,
			type: "number",
			align: "right",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell render="absolute" reference={reference} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
		{
			field: "chargebackCommission",
			headerName: "C.B. Comm.",
			width: 86,
			type: "number",
			align: "right",
			headerClassName: HEADER_CLASS_NAME,
			cellClassName: getCellClassNameFn(issues, {}, {}),
			renderCell: (params) => <TypographyCell render="absolute" reference={reference} params={params} issues={issues} />,
			renderHeader: (params) => <TypographyHeader params={params} />,
		},
	];
}

function ChargesTable(props: Readonly<ChargesTableProps>) {
	const { hideFooter = true } = props;
	const title = props.title ?? "Chargebacks";
	const apiRef = useGridApiRef();
	const [totalColumns, setTotalColumns] = useState<GridColDef[]>([]);
	// Issues support attributes
	const [openAddIssues, setOpenAddIssues] = useState(false);
	const [anchorAddIssues, setAnchorAddIssues] = useState<(HTMLDivElement & EventTarget) | null>(null);
	const [issues, setIssues] = useState<Issue[]>([]);
	const activeIssue = useRef<Issue>(null);

	const handleClickCell: MouseEventHandler<HTMLDivElement> = (event) => {
		activeIssue.current = {
			rowReference: event.currentTarget.parentElement?.dataset.id, // _id
			columnReference: event.currentTarget.dataset.field, // field
		};
		setAnchorAddIssues(event.currentTarget);
		setOpenAddIssues(true);
	};

	const handleChangeIssues = (color: Nullable<string>) => {
		let currentIssues = [...issues];
		if (color) {
			let isPresent = false;
			for (const issue of currentIssues) {
				if (
					issue &&
					issue.rowReference === activeIssue?.current?.rowReference &&
					issue.columnReference === activeIssue?.current?.columnReference
				) {
					issue.color = color;
					isPresent = true;
					break;
				}
			}

			if (!isPresent) {
				currentIssues.push({
					color,
					tableReference: props?.issuesReference,
					rowReference: activeIssue?.current?.rowReference,
					columnReference: activeIssue?.current?.columnReference,
				});
			}
		} else {
			currentIssues?.forEach((issue, key) => {
				if (
					issue &&
					issue.rowReference === activeIssue?.current?.rowReference &&
					issue.columnReference === activeIssue?.current?.columnReference
				) {
					currentIssues[key] = null;
				}
			});
			currentIssues = currentIssues.filter((issue) => issue);
		}

		setIssues(currentIssues);
		props?.onIssuesUpdatedAction?.(currentIssues, props?.issuesReference);
	};

	useMemo(() => {
		setTotalColumns(getInitialColumns(props?.issuesReference ?? "", issues || []));
	}, [props?.issuesReference, issues]);

	useEffect(() => {
		// Clear filter on purpose
		try {
			const filter = apiRef.current?.state?.filter;
			if (filter) {
				const cleanFilter = {
					filterModel: { items: [], linkOperator: "and" },
					visibleRows: null,
					visibleRowsLookup: {},
				};

				apiRef.current.setState((prevState) => ({
					...prevState,
					filter: { ...filter, ...cleanFilter },
				}));
			}
		} catch (error) {
			errorLogger(getError(error));
		}

		setIssues(props.issues ?? []);

		return () => {
			setTotalColumns([]);

			// Issues controls
			setIssues([]);
			activeIssue.current = null;
			setOpenAddIssues(false);
			setAnchorAddIssues(null);
		};
	}, [props, apiRef]);

	if (!totalColumns) {
		return null;
	}

	function getCell(): Partial<GridCellProps> | undefined {
		if (props?.issues) {
			return { onClick: handleClickCell };
		}
	}

	return (
		<>
			<DataSmartTable
				{...props.tableProps}
				apiRef={apiRef}
				columns={totalColumns}
				rows={props.data ?? []}
				disableColumnMenu
				disableRowSelectionOnClick
				disableMultipleRowSelection
				checkboxSelection={false}
				hideFooter={hideFooter || true}
				columnVisibilityModel={{
					_id: false,
				}}
				initialState={{
					aggregation: {
						model: {
							chargebackInvoiceAmount: "sum",
							saleGross: "sum",
							chargebackAmount: "sum",
							chargebackCommission: "sum",
						},
					},
				}}
				slots={{
					toolbar: CommonToolbar,
					noRowsOverlay: NoRowsOverlay,
				}}
				slotProps={{
					toolbar: {
						customTitle: {
							hide: false,
							label: title,
							sx: { mb: "15px" },
						},
					},
					noRowsOverlay: {
						title: `No ${title} Data`,
					},
					cell: getCell(),
				}}
				customProps={{}}
			/>
			{props.addIssues && (
				<AddCellIssues
					open={openAddIssues}
					anchorEl={anchorAddIssues}
					onCloseAction={() => {
						setOpenAddIssues(false);
						setAnchorAddIssues(null);
					}}
					onChangeAction={handleChangeIssues}
				/>
			)}
		</>
	);
}

export default function ChargebacksTable(props: Readonly<ChargesTableProps>) {
	if (props.loading) {
		return <TableLoader smallTitle content />;
	}

	return <>{props.dataPresent && <ChargesTable {...props} />}</>;
}
